import { useRef, useState, type FormEvent } from 'react'
import { ArrowRight, CheckCircle2 } from 'lucide-react'
import { ApiError, api } from '../../lib/api'
import { useI18n } from '../../lib/i18n'
import { useAppRuntime } from '../../lib/app-runtime'

export function ContactMessageForm() {
  const { locale } = useI18n()
  const ar = locale === 'ar'
  const runtime = useAppRuntime()
  // Direct contact links expose the form in the server HTML as well as after
  // hydration; ordinary homepage visits retain the compact disclosure.
  const directContact = new URL(runtime.url).searchParams.get('contact') === '1'
  const Container = directContact ? 'section' : 'details'
  const Heading = directContact ? 'h3' : 'summary'
  const [pending, setPending] = useState(false)
  const [error, setError] = useState('')
  const [reference, setReference] = useState('')
  const [uncertain, setUncertain] = useState(false)
  const attempt = useRef<{ payload: string; key: string } | null>(null)
  const inFlight = useRef(false)

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (inFlight.current || uncertain) return
    const data = new FormData(event.currentTarget)
    const input = { name: data.get('name'), email: data.get('email'), message: data.get('message'),
      locale: ar ? 'ar' : 'en', website: data.get('website') }
    const payload = JSON.stringify(input)
    if (attempt.current?.payload !== payload) attempt.current = { payload, key: crypto.randomUUID() }
    inFlight.current = true
    setPending(true)
    setError('')
    try {
      const result = await api<{ reference: string }>('/api/contact-messages/', {
        method: 'POST', body: JSON.stringify({ ...input, requestKey: attempt.current.key }),
      })
      setReference(result.reference)
    } catch (failure) {
      const status = failure instanceof ApiError ? failure.status : 0
      const deliveryUncertain = failure instanceof ApiError && failure.code === 'contact_message_uncertain'
      setUncertain(deliveryUncertain)
      setError(deliveryUncertain
        ? (ar ? 'تعذّر تأكيد التسليم. قد تكون رسالتك وصلت؛ لن نكرر إرسال هذا الطلب تلقائيًا.' : 'Delivery is unconfirmed. Your message may have arrived; this request will not be sent again automatically.')
        : status === 400
        ? (ar ? 'راجع الاسم والبريد الإلكتروني ونص الرسالة.' : 'Check your name, email address and message.')
        : status === 429
          ? (ar ? 'وصلنا عدد كبير من الرسائل. حاول مرة أخرى لاحقًا.' : 'We have received too many messages. Please try again later.')
          : status === 409
            ? (ar ? 'رسالتك قيد المعالجة. انتظر قليلًا ثم أعد المحاولة.' : 'Your message is being processed. Wait a moment, then retry.')
            : (ar ? 'تعذّر تأكيد إرسال رسالتك. بقيت بياناتك هنا؛ حاول مرة أخرى.' : 'We could not confirm sending your message. Your details are still here; please retry.'))
    } finally { inFlight.current = false; setPending(false) }
  }

  return <Container className="contact-message" id="contact">
    <Heading className="contact-message__heading">{ar ? 'لديك سؤال؟ تواصل معنا' : 'Have a question? Contact us'}</Heading>
    {reference ? <div className="pilot-request contact-message__body" role="status" aria-live="polite">
      <CheckCircle2 size={24} aria-hidden="true" />
      <h3>{ar ? 'أُرسلت رسالتك' : 'Your message was sent'}</h3>
      <p>{ar ? 'سنرد على البريد الإلكتروني الذي أدخلته.' : 'We will reply to the email address you provided.'}</p>
      <p>{ar ? 'رقم الرسالة:' : 'Message reference:'} <span className="pilot-request__reference" dir="ltr">{reference}</span></p>
    </div> : <form className="pilot-request contact-message__body" onSubmit={(event) => void submit(event)} onChange={() => { setUncertain(false); setError('') }} aria-label={ar ? 'تواصل معنا' : 'Contact us'}>
      <p>{ar ? 'اكتب سؤالك أو رسالتك، وسيرد عليك فريق سِعَة عبر البريد الإلكتروني.' : 'Send your question or message. The Seaa team will reply by email.'}</p>
      <fieldset disabled={pending}>
        <label>{ar ? 'الاسم' : 'Name'}<input name="name" autoComplete="name" minLength={2} maxLength={100} required dir="auto" /></label>
        <label>{ar ? 'البريد الإلكتروني' : 'Email address'}<input name="email" type="email" autoComplete="email" maxLength={254} required dir="ltr" /></label>
        <label>{ar ? 'رسالتك' : 'Message'}<textarea name="message" rows={4} minLength={10} maxLength={4000} required dir="auto" /></label>
        <label className="pilot-request__trap" aria-hidden="true">Website<input name="website" tabIndex={-1} autoComplete="off" /></label>
        <p>{ar ? 'نستخدم بياناتك للرد على رسالتك فقط.' : 'We use your details only to respond to your message.'} <a href={`/privacy?lang=${locale}`}>{ar ? 'الخصوصية' : 'Privacy'}</a></p>
        <button className="marketing-button marketing-button--primary" type="submit" disabled={uncertain}>{pending ? (ar ? 'جارٍ الإرسال…' : 'Sending…') : uncertain ? (ar ? 'التسليم غير مؤكد' : 'Delivery unconfirmed') : (ar ? 'إرسال الرسالة' : 'Send message')}<ArrowRight size={17} aria-hidden="true" /></button>
      </fieldset>
      {error && <p className="pilot-request__error" role="alert">{error}</p>}
    </form>}
  </Container>
}
